import React from "react";
import styled from "styled-components";
import { Stat, StatValue, StatLabel, Muted } from "../shared";
import { compact, StatStripData } from "./homeDerive";

export type StatKey = "views" | "avg" | "engagement" | "week";

const Strip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-2);
`;

const Tile = styled.button<{ $selected: boolean }>`
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  background: var(--color-surface-subtle);
  border: 1px solid
    ${(p) => (p.$selected ? "var(--color-border-strong)" : "transparent")};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--color-border-strong);
  }
`;

export type StatStripProps = {
  data: StatStripData;
  loading: boolean;
  selected: StatKey | null;
  onSelect: (key: StatKey) => void;
};

export const StatStrip: React.FC<StatStripProps> = ({
  data,
  loading,
  selected,
  onSelect,
}) => {
  const dash = loading || data.measured === 0;
  const tiles: { key: StatKey; value: React.ReactNode; label: string }[] = [
    {
      key: "views",
      value: dash ? "–" : compact(data.views),
      label: "Views",
    },
    {
      key: "avg",
      value: dash ? "–" : compact(data.avgViews),
      label: "Avg views / post",
    },
    {
      key: "engagement",
      value:
        dash || data.engagementRate === null
          ? "–"
          : `${(data.engagementRate * 100).toFixed(1)}%`,
      label: "Engagement rate",
    },
    {
      key: "week",
      value: (
        <>
          {loading ? "–" : data.postedThisWeek}
          {!loading && data.plannedThisWeek !== null && (
            <Muted style={{ fontSize: 13 }}> / {data.plannedThisWeek} planned</Muted>
          )}
        </>
      ),
      label: "Posts this week",
    },
  ];

  return (
    <Strip>
      {tiles.map((t) => (
        <Tile
          key={t.key}
          type="button"
          $selected={selected === t.key}
          onClick={() => onSelect(t.key)}
        >
          <Stat>
            <StatValue>{t.value}</StatValue>
            <StatLabel>{t.label}</StatLabel>
          </Stat>
        </Tile>
      ))}
    </Strip>
  );
};

export default StatStrip;
